// frontend/src/projectToDataModel.ts
import { ProjectDetail } from "./model/backendTypes";
import { xmlToData } from "./xmlHandling";
import { DataModel, defaultData, JsonValue, JsonObject } from "./AppTypes";

/**
 * Parse a JSON string safely and normalize to an array.
 * Accepts:
 *  - JSON array: [...]
 *  - JSON object with .entries or .data as array: { entries: [...] } / { data: [...] }
 * Returns:
 *  - array if possible
 *  - null otherwise
 */
function parseJsonArrayOrNull(raw?: string | null): JsonObject[] | null {
  if (raw == null) return null;

  const s = String(raw).trim();
  if (!s) return null;

  try {
    const parsed = JSON.parse(s);

    const arr =
      Array.isArray(parsed) ? parsed
      : parsed && typeof parsed === "object" && Array.isArray((parsed as any).entries) ? (parsed as any).entries
      : parsed && typeof parsed === "object" && Array.isArray((parsed as any).data) ? (parsed as any).data
      : null;

    if (!arr) return null;

    // Enforce: array of objects only
    const objs = arr.filter((x: any) => x && typeof x === "object" && !Array.isArray(x));
    return objs as JsonObject[];
  } catch {
    return null;
  }
}

/**
 * Ensure that DataModel always has the required "runtime" fields,
 * even if xmlToData() returns an older shape.
 */
function normalizeDataModelBase(dm: any): DataModel {
  return {
    ...defaultData,
    ...dm,
    fulfillments_json:
      dm?.fulfillments_json === undefined ? defaultData.fulfillments_json : dm.fulfillments_json,
    syntaxchecks_json:
      dm?.syntaxchecks_json === undefined ? defaultData.syntaxchecks_json : dm.syntaxchecks_json,
  };
}

/**
 * Single source of truth:
 * ProjectDetail (Backend) -> DataModel (Frontend)
 */
export function projectToDataModel(proj: ProjectDetail): DataModel {
  // xmlToData kann null liefern -> dann mit defaultData weiterarbeiten
  const parsed = xmlToData(proj.rules_xml);
  const dm = normalizeDataModelBase(parsed ?? {});

  dm.rules_xml = proj.rules_xml;
  dm.plan_csv = proj.plan_csv ?? "";

  // IMPORTANT: enforce array-or-null, never object/string
  dm.fulfillments_json = parseJsonArrayOrNull(proj.fulfillments_json);
  dm.syntaxchecks_json = parseJsonArrayOrNull(proj.syntaxchecks_json);

  return dm;
}

/**
 * Patch update for plan polling results:
 * plan_csv + JSON blobs (strings) werden sicher geparst.
 *
 * IMPORTANT: only overwrite fulfillments/syntax if the field is actually present in patch
 * (so we don't accidentally wipe values when patch omits them).
 */
export function applyPlanPatch(
  prev: DataModel,
  patch: {
    plan_csv?: string | null;
    fulfillments_json?: string | null;
    syntaxchecks_json?: string | null;
  }
): DataModel {
  const next: DataModel = {
    ...prev,
    plan_csv: patch.plan_csv ?? "",
  };

  if (Object.prototype.hasOwnProperty.call(patch, "fulfillments_json")) {
    next.fulfillments_json = parseJsonArrayOrNull(patch.fulfillments_json);
  }

  if (Object.prototype.hasOwnProperty.call(patch, "syntaxchecks_json")) {
    next.syntaxchecks_json = parseJsonArrayOrNull(patch.syntaxchecks_json);
  }

  return next;
}